import { useAuth } from '@clerk/clerk-react';
import { useCallback, useEffect, useRef, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_URL;

// List of product-metadata records, optionally filtered by search/type/family.
export function useProductList({ search = '', type = '', family = '', company = '', limit = 500, enabled = true } = {}) {
  const { getToken } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchList = useCallback(async () => {
    if (!enabled) return;
    setIsLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const params = new URLSearchParams();
      if (search.trim()) params.set('search', search.trim());
      if (type) params.set('type', type);
      if (family) params.set('family', family);
      if (company) params.set('companyCode', company);
      params.set('limit', String(limit));
      const res = await fetch(`${API_BASE}/product-meta?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed to load products (${res.status})`);
      const data = await res.json();
      setItems(Array.isArray(data.items) ? data.items : []);
      setTotal(Number(data.total) || 0);
    } catch (err) {
      setError(err);
      setItems([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  }, [getToken, search, type, family, company, limit, enabled]);

  const ref = useRef(fetchList);
  ref.current = fetchList;
  useEffect(() => {
    const handle = setTimeout(() => ref.current(), 250);
    return () => clearTimeout(handle);
  }, [search, type, family, company, limit, enabled]);

  return { items, total, isLoading, error, refetch: useCallback(() => ref.current(), []) };
}

// All families (familyKey + productCount + profile) — drives the family selector.
export function useProductFamilies({ enabled = true } = {}) {
  const { getToken } = useAuth();
  const [families, setFamilies] = useState([]);
  const [typeCounts, setTypeCounts] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const fetchFamilies = useCallback(async () => {
    if (!enabled) return;
    setIsLoading(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/product-families`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setFamilies(Array.isArray(data.items) ? data.items : []);
      setTypeCounts(data.typeCounts || {});
    } catch {
      setFamilies([]);
      setTypeCounts({});
    } finally {
      setIsLoading(false);
    }
  }, [getToken, enabled]);

  const ref = useRef(fetchFamilies);
  ref.current = fetchFamilies;
  useEffect(() => { ref.current(); }, [enabled]);

  return { families, typeCounts, isLoading, refetch: useCallback(() => ref.current(), []) };
}

// Per-family analytics (production volume, RMA rate, MTBF), grouped by the live
// device productFamily values.
export function useFamilyAnalytics({ enabled = true } = {}) {
  const { getToken } = useAuth();
  const [analytics, setAnalytics] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchAnalytics = useCallback(async () => {
    if (!enabled) return;
    setIsLoading(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/product-families/analytics`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setAnalytics(Array.isArray(data.items) ? data.items : []);
    } catch {
      setAnalytics([]);
    } finally {
      setIsLoading(false);
    }
  }, [getToken, enabled]);

  const ref = useRef(fetchAnalytics);
  ref.current = fetchAnalytics;
  useEffect(() => { ref.current(); }, [enabled]);

  return { analytics, isLoading, refetch: useCallback(() => ref.current(), []) };
}

// Create/update a family profile. Returns the saved profile.
export async function saveFamily(getToken, familyKey, payload) {
  const token = await getToken();
  const res = await fetch(`${API_BASE}/product-families/${encodeURIComponent(familyKey)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || `Save failed (${res.status})`);
  }
  const data = await res.json();
  return data.item;
}

// Company-code registry (2-digit middle code → company + warranty months).
export function useCompanyCodes({ enabled = true } = {}) {
  const { getToken } = useAuth();
  const [codes, setCodes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchCodes = useCallback(async () => {
    if (!enabled) return;
    setIsLoading(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/company-codes`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setCodes(Array.isArray(data.items) ? data.items : []);
    } catch {
      setCodes([]);
    } finally {
      setIsLoading(false);
    }
  }, [getToken, enabled]);

  const ref = useRef(fetchCodes);
  ref.current = fetchCodes;
  useEffect(() => { ref.current(); }, [enabled]);

  return { codes, isLoading, refetch: useCallback(() => ref.current(), []) };
}

export async function saveCompanyCode(getToken, code, payload) {
  const token = await getToken();
  const res = await fetch(`${API_BASE}/company-codes/${encodeURIComponent(code)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || `Save failed (${res.status})`);
  }
  return (await res.json()).item;
}

export async function deleteCompanyCode(getToken, code) {
  const token = await getToken();
  const res = await fetch(`${API_BASE}/company-codes/${encodeURIComponent(code)}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || `Delete failed (${res.status})`);
  }
}

// Manufacturer-code registry (service-tag leading letter → factory).
export function useManufacturerCodes({ enabled = true } = {}) {
  const { getToken } = useAuth();
  const [letters, setLetters] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchLetters = useCallback(async () => {
    if (!enabled) return;
    setIsLoading(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/manufacturer-codes`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setLetters(Array.isArray(data.items) ? data.items : []);
    } catch {
      setLetters([]);
    } finally {
      setIsLoading(false);
    }
  }, [getToken, enabled]);

  const ref = useRef(fetchLetters);
  ref.current = fetchLetters;
  useEffect(() => { ref.current(); }, [enabled]);

  return { letters, isLoading, refetch: useCallback(() => ref.current(), []) };
}

export async function saveManufacturerCode(getToken, letter, payload) {
  const token = await getToken();
  const res = await fetch(`${API_BASE}/manufacturer-codes/${encodeURIComponent(letter)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || `Save failed (${res.status})`);
  }
  return (await res.json()).item;
}

export async function deleteManufacturerCode(getToken, letter) {
  const token = await getToken();
  const res = await fetch(`${API_BASE}/manufacturer-codes/${encodeURIComponent(letter)}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || `Delete failed (${res.status})`);
  }
}

// Single product-metadata record by product number.
export function useProductDetail(productNumber) {
  const { getToken } = useAuth();
  const [item, setItem] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchOne = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/product-meta/${encodeURIComponent(productNumber)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 404) { setItem(null); setError(new Error('Product not found')); return; }
      if (!res.ok) throw new Error(`Failed to load product (${res.status})`);
      const data = await res.json();
      setItem(data.item || null);
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, [getToken, productNumber]);

  useEffect(() => { fetchOne(); }, [fetchOne]);

  return { item, isLoading, error, refetch: fetchOne, setItem };
}

// Import one or many product JSONs (drag-and-drop). Returns { created, updated, errors }.
export async function importProducts(getToken, products) {
  const token = await getToken();
  const res = await fetch(`${API_BASE}/product-meta/import`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ products }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Import failed (${res.status})`);
  return data;
}

// Add or remove a supplier key across many products at once (admin + service).
// payload: { supplier, action:'add'|'remove', type?, family? }. No type/family
// applies to all products. Returns { matched, modified }.
export async function assignSupplierBulk(getToken, payload) {
  const token = await getToken();
  const res = await fetch(`${API_BASE}/product-meta/suppliers/bulk`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Failed (${res.status})`);
  return data;
}

// Create/update a product-metadata record. Returns the saved record.
export async function saveProduct(getToken, productNumber, payload) {
  const token = await getToken();
  const res = await fetch(`${API_BASE}/product-meta/${encodeURIComponent(productNumber)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || `Save failed (${res.status})`);
  }
  const data = await res.json();
  return data.item;
}
